import Vue from 'vue'
import config from '@/config'
import axios from 'axios'
import moment from 'moment'
import alertHelper from '@/helpers/alert'
import paymentHelper from '@/helpers/payment'

const state = {
  payments: [],
  querySearch: '',
  selectedPayment: {}
}

const getters = {
  getPayments(state) {
    return state.payments
  },
  getQuerySearch(state) {
    return state.querySearch
  },
  getSelectedPayment(state) {
    return state.selectedPayment
  }
}

const mutations = {
  setPayments(state, payments) {
    Vue.set(state, 'payments', payments)
  },
  setQuerySearch(state, querySearch) {
    Vue.set(state, 'querySearch', querySearch)
    // console.log('setQuerySearch', {querySearch})
  },
  setSelectedPayment(state, payment) {
    Vue.set(state, 'selectedPayment', payment)
  },
  updateSelectedPayment(state, properties) {
    const selectedPayment = Object.assign({}, state.selectedPayment, properties);
    Vue.set(state, 'selectedPayment', selectedPayment)
    // console.log({updateSelectedPayment: state.selectedPayment});
  },
  updatePayments(state, payments) {
    const existingPayments = state.payments
    // Keep a map of ids so we can track the ids to update
    const updatedPaymentsIds = payments.map(p => p.id)

    // Loop through the existing payments and merge the new data
    existingPayments.forEach(ep => {
      if (updatedPaymentsIds.indexOf(ep.id) !== -1) {
        Object.assign(ep, payments[updatedPaymentsIds.indexOf(ep.id)])
      }
    })

    state.payment = existingPayments
  }
}

const actions = {
  async getPayments(context, params) {
    const url = config.apiBaseUrl + 'payment'
    try {
      await context.dispatch('HouseList/list', {usage: 'forPayments'}, { root: true })
      params = await context.dispatch('getPaymentParameters', params)
      const response = await axios.get(url, {params})
      const houses = context.rootGetters['HouseList/getHouseList']
      if (response.data.errno === 0) {
        const payments = mergePaymentsWithHouses(
          response.data.data,
          houses
        )
        context.commit('setPayments', payments)
        context.commit('setQuerySearch', params.querySearch)
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch(error) {
      console.log('getPayments', {params, error})
      alertHelper.error(
        'Sorry, we are unable to complete the request. Please refresh your browser and try again:' + error.toString()
      )
    }
  },
  async getPayment(context, id) {
    try {
      const response = await axios.get(config.apiBaseUrl + 'payment/' + id)

      if (response.data.errno === 0) {
        let payment = response.data.data
        const house = await axios.get(
          config.apiBaseUrl + 'house/findById',
          { params: { id: response.data.data.house_id } }
        )
        const lease = await axios.get(
          config.apiBaseUrl + 'lease',
          { params: { id: response.data.data.lease_id} }
        )

        payment.unit = house.data.data.unit
        payment.num = house.data.data.num
        payment.street = house.data.data.street
        payment.owner_firstname = house.data.data.owner_firstname
        payment.owner_lastname = house.data.data.owner_lastname
        payment.owner_phone = house.data.data.owner_phone
        payment.owner_email = house.data.data.owner_email
        if (payment.state === 0) {
          payment.fee = house.data.data.fee.filter(f => f.state === 0);
        } else {
          payment.fee = house.data.data.fee.filter(f => f.state === 1 && f.payment_id === payment.id);
        }
        // console.log('payment.fee', { payment_fee : payment.fee, payment, house: house.data.data})

        // We only support one tenant per lease atm
        payment.tenant = lease.data.data.tenant[0]

        context.commit('setSelectedPayment', payment)
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch(error) {
      alertHelper.error(
        'Sorry, we are unable to complete the request. Please refresh your browser and try again:' + error.toString()
      )
    }
  },
  async getLastBalance(context, payment) {
    try {
      const response = await axios.get(config.apiBaseUrl + 'payment/getLastBalance', {params: { id: payment.id }})
      console.log(response.data.data);
      return response.data.data.lastBalance
    } catch (error) {
      alertHelper.error(
        'Sorry, we are unable to get last balance. Please refresh your browser and try again.'
      )
    }
  },
  async savePayment(context, payment) {
    try {
      payment.fee.forEach((fee) => {
        fee.payment_id = payment.id
        fee.state = 1
      })
      const response = await axios.put(
        config.apiBaseUrl + 'payment/' + payment.id, payment
      )

      if (response.data.errno === 0) {
        context.commit('updatePayments', [payment])
        context.commit('setSelectedPayment', payment)

        // Update the payment fees
        if (payment.fee.length > 0) {
          payment.fee.forEach((fee) => {
            fee.payment_id = payment.id
            fee.state = 1
            context.commit('HouseList/Fees/update', {fee}, {
              root: true,
            })
          })
        }

        alertHelper.success('Payment saved successfully')
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async downloadPayment(context, payment) {
    try {
      await axios
        .post(
          config.apiBaseUrl + 'payment/downloadPDF',
          { id: payment.id },
          { responseType: 'blob' }
        )
        .then((response) => {
          let blob = new Blob([response.data], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)

          let newTab = window.open()
          newTab.location.href = url
          setTimeout(() => {
            if (newTab.closed) {
              let link = document.createElement('a')
              link.href = url
              link.download = payment.id + '.pdf'
              // document.body.appendChild(link)
              link.click()
            }
          }, 200)
        })
    } catch (error) {
      alertHelper.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async sendPayment(context, payment) {
    try {
      // console.log('sendPayment', payment)
      const payment_properties = {...payment }
      delete payment_properties.fee
      delete payment_properties.tenant

      const response = await axios.post(
        config.apiBaseUrl + 'payment/sendPayment',
        { data: [payment_properties] }
      )

      if (response.data.errno === 0) {
        context.commit('updatePayments', [{ ...payment_properties, payment_pdf_send: 1}])
        alertHelper.success('Report sent successfully to the owner')
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async archivePayment(context, payment) {
    // console.log('store.payment.archivepayment')
    try {
      const payment_properties = {...payment }
      delete payment_properties.fee
      delete payment_properties.tenant

      const response = await axios.post(
        config.apiBaseUrl + 'payment/archivePayment',
        payment_properties
      )

      if (response.data.errno === 0) {
        context.commit('updateSelectedPayment', { ...payment_properties, payment_pdf_send: 1})
        alertHelper.success('Payment archived successfully')
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async redoPayment(context, payment) {
    // console.log({ Payment_redoPayment: payment })
    try {
      if (payment.fee.length > 0) {
        payment.fee.forEach((fee) => {
          fee.payment_id = ''
          fee.state = 0
          context.dispatch(
            'HouseList/Fees/update',
            {fee},
            { root: true }
          )
        })
      }

      payment.state = '0'
      payment.pay_time = null
      payment.fee = []

      const response = await axios.put(
        config.apiBaseUrl + 'payment/' + payment.id,
        payment
      )

      if (response.data.errno === 0) {
        context.commit('updatePayments', [payment])
        alertHelper.success('Payment is reverted to unprocessed state')
      } else {
        alertHelper.error(response.data.errmsg)
      }

      // 30/4/2021
      const houses = context.rootGetters['HouseList/getHouseList']
      if (!houses) {
        throw 'HouseList/getHouseList returns null'
      }
      const house = houses.find(h => h.id === payment.house_id);
      if (!houses) {
        throw `HouseList/getHouseList does not contain id of ${payment.house_id}`
      }
      if (payment.state == 0) {
        console.log({fees: house.fee});
        house.fee.forEach((f) => {
          if (f.payment_id === payment.id) {
            f.payment_id = '';
            f.state = 0;
          }
        });
        const unpaidFees = house.fee.filter(f => f.state === 0);
        payment.unpaidFeesCount = unpaidFees.length;
        payment.fee = unpaidFees   // 4-28-2021
        const curLease = house.lease.find(lease => lease.id === payment.lease_id);
        payment.balance = curLease? curLease.balance : 0;
      }
    } catch (error) {
      alertHelper.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async bulkUpdate(context, payments) {
    try {
      const paymentResponse = await axios.put(
        config.apiBaseUrl + 'payment/putMany',
        payments
      )

      if (paymentResponse.data.errno === 0) {
        context.commit('updatePayments', payments)
        alertHelper.success('Payments are processed successfully')
      } else {
        alertHelper.error(paymentResponse.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
  },
  async processPayments(context, payments) {
    try {
      const paymentResponse = await axios.post(
        config.apiBaseUrl + 'payment/processPayments',
        payments
      )

      if (paymentResponse.data.errno === 0) {
        payments = paymentResponse.data.data;
        context.commit('updatePayments', payments)
        alertHelper.success('Payments are processed successfully')
      } else {
        alertHelper.error(paymentResponse.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
  },
  /**
   * Downloads the bank file for payments passed
   * Because this is a SPA, we have to read the response type and use
   * some blob magic to download the file
   *
   * @param {object} context
   * @param {array} payments ids of payments
   */
  async downloadBankFile(context, payments) {
    if (payments.length === 0) {
      alertHelper.error('Please select payments to generate the bank file')
      return
    } else if (paymentHelper.hasUnprocessedPayments(payments)) {
      alertHelper.error('Bank file cannot be generated with unprocessed payments. Please un-select or process the payments')
      return
    } else if (paymentHelper.hasCashPays(payments)) {
      alertHelper.error('Account of cash pay shopuld be selected to generate bank files. Please un-select or process the payments')
      return
    } else if (paymentHelper.hasNegativePayments(payments)) {
      alertHelper.error('Bank file cannot be generated with negative payments. Please un-select or process the payments')
      return
    } else if (paymentHelper.hasUnknownOwnerAccount(payments)) {
      alertHelper.error('Bank file cannot be generated with invalid bank accounts. Please un-select or process the payments')
      return
    }

    let datetimestamp = moment()
    try {
      // Backend only needs an array of ids
      const ids = payments.map(p => p.id)
      const response = await axios.post(
        config.apiBaseUrl + 'payment/checkDownloadCSV',
        { paymentIdList: ids }
      )
      console.log('payment/checkDownloadCSV', response)
      if (response.status !== 200 || response.data.errno !== 0) {
        alertHelper.error(response.data.errmsg)
        return;
      }
      if (!!response.data.data.datetimestamp) {
        datetimestamp = response.data.data.datetimestamp
      }
    } catch (error) {
      alertHelper.error(error.toString())
      return;
    }

    try {
      // Backend only needs an array of ids
      const ids = payments.map(p => p.id)
      const response = await axios.post(
        config.apiBaseUrl + 'payment/downloadCSV',
        { paymentIdList: ids, datetimestamp },
        { responseType: 'blob' }
      )

      console.log('payment/downloadCSV', response.data);
      if (response.status === 200) {
        const stamp = moment(datetimestamp).format("DDMMMyyyy-HH.mm.ss")
        // Get the type of data from response and set the file extension
        const fileType = (response.data.type) === 'text/csv' ? '.csv' : (response.data.type) === 'application/json' ? '.json' : '.zip'
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${stamp}${fileType}`);
        document.body.appendChild(link);
        link.click();
        if (fileType === '.json') {
            throw `Failed to generate banking files, please check the download result.`
        }
        payments.forEach(p => {
          p.pay_time = datetimestamp;
          p.bankfiled = 1;
        });
        context.commit('updatePayments', payments);
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
  },
  /**
   * Resets the storage
   */
  clear(context) {
    context.commit('setPayments', [])
    context.commit('setSelectedPayment', {})
  },
  getPaymentParameters(context, {querySearch, startDate, endDate}) {
    let params = {
        paydue_time_begin: '',
        paydue_time_end: '',
        state: ''
    }
    querySearch = querySearch || 'current_week'

    if (querySearch === 'overdue') {
      params = {
        paydue_time_begin: moment().subtract(26, 'week').startOf('week').format('YYYY-MM-DD'),
        paydue_time_end: moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD HH:mm:ss'),
        payment_pdf_send: 0,
        state: '0'
      }
    } else if (querySearch === 'current_week') {
      params = {
        paydue_time_begin: moment().startOf('week').format('YYYY-MM-DD'),
        paydue_time_end: moment().endOf('week').format('YYYY-MM-DD HH:mm:ss'),
        payment_pdf_send: 0,
        state: '0'
      }
    } else if (querySearch === 'processed') {
      params = {
        paydue_time_begin: moment().subtract(26, 'week').startOf('week').format('YYYY-MM-DD'),
        paydue_time_end: moment().endOf('week').format('YYYY-MM-DD HH:mm:ss'),
        payment_pdf_send: 0,
        bankfiled: 0,
        state: '1'
      }
    } else if (querySearch === 'bankfiled') {
      params = {
        paydue_time_begin: moment().startOf('week').format('YYYY-MM-DD'),
        paydue_time_end: moment().endOf('week').format('YYYY-MM-DD HH:mm:ss'),
        payment_pdf_send: 0,
        bankfiled: 1,
        state: '1'
      }
    } else {
      // console.log('getPaymentParameters', {startDate, endDate})
      params = {
        paydue_time_begin: (startDate? moment(startDate): moment().subtract(1, 'week').startOf('week')).format('YYYY-MM-DD'),
        paydue_time_end: (endDate? moment(endDate): moment().endOf('week')).format('YYYY-MM-DD HH:mm:ss'),
        state: '0,1,9'
      }
    }
    Object.assign(params, {querySearch: querySearch})
    return params
  }


}

function mergePaymentsWithHouses(payments, houses) {
  const housesMap = []
  const paymentsMap = []

  houses.filter(h => !h.testing).forEach(house => {
    housesMap[house.house_id] = house
  })

  payments.forEach(payment => {
    if (typeof housesMap[payment.house_id] !== 'undefined') {
      const house = housesMap[payment.house_id];
      // console.log({mergePaymentsWithHouses: house})
      payment.unit = house.unit
      payment.num = house.num
      payment.street = house.street
      payment.suburb = house.suburb
      payment.city = house.city
      payment.country = house.country
      payment.owner_bank_account = house.owner_bank_account
      if (payment.state === 0) {
        const disactived = house.fee.filter(f => f.state === 99);
        payment.disactived = disactived

        const unpaidFees = house.fee.filter(f => f.state === 0 && !f.payment_id);
        payment.unpaidFeesCount = unpaidFees.length;
        payment.fee = unpaidFees   // 4-28-2021
        for(const f of unpaidFees) {
          f.payment_id = payment.id;
        }
        const curLease = house.lease.find(lease => lease.id === payment.lease_id);
        payment.balance = curLease? curLease.balance : 0;
      }
      paymentsMap.push(payment)
      // console.log(`housesMap[${payment.house_id}]`, {house, payment})
    } else {
      console.log('house_id not in house map of payment', { payment })
    }
  })

  return paymentsMap
}

const storeModule = {
  namespaced: true,
  state: state,
  getters: getters,
  actions: actions,
  mutations: mutations,
}

export default storeModule
